import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { PageProps } from "../types/auth";

import styles from './styles/ImportCurriculumPage.module.css';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

interface Level {
  name: string;
  skills: { name: string }[];
}

interface CurriculumImport {
  name: string;
  levels_sequence: Level[];
}

const ImportCurriculumPage: React.FC<PageProps> = ({ token, selectedOrgID }) => {
  const [name, setName] = useState<string>("");
  const [importText, setImportText] = useState<string>("");
  const [previewData, setPreviewData] = useState<Level[] | null>(null);
  const [error, setError] = useState<string>("");
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const navigate = useNavigate();
  const nameInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (nameInputRef.current) {
      nameInputRef.current.focus();
    }
  }, []);

  const parseImportText = (text: string): Level[] => {
    const lines = text.split('\n').map(line => line.trim()).filter(line => line !== '');
    const levels: Level[] = [];
    let currentLevel: Level | null = null;

    for (const line of lines) {
      if (!line.startsWith('-')) {
        // This is a level name
        if (currentLevel) {
          levels.push(currentLevel);
        }
        currentLevel = { name: line, skills: [] };
      } else if (currentLevel) {
        // This is a skill
        const skillName = line.substring(1).trim();
        if (skillName) {
          currentLevel.skills.push({ name: skillName });
        }
      }
    }

    // Add the last level if it exists
    if (currentLevel) {
      levels.push(currentLevel);
    }

    return levels;
  };

  const handlePreview = () => {
    try {
      const levels = parseImportText(importText);
      if (levels.length === 0) {
        setError("No valid levels found in the import text.");
        setPreviewData(null);
        return;
      }
      
      setPreviewData(levels);
      setError("");
    } catch (err) {
      setError("Failed to parse import text. Please check the format.");
      setPreviewData(null);
    }
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    
    if (!name.trim()) {
      setError("Curriculum name is required.");
      return;
    }

    if (!previewData || previewData.length === 0) {
      setError("Please preview the curriculum first.");
      return;
    }

    setIsSubmitting(true);

    try {
      const curriculumData: CurriculumImport = {
        name,
        levels_sequence: previewData
      };

      const response = await fetch(`${API_BASE_URL}/api/curriculums/import`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
          "X-Org-ID": selectedOrgID?.toString() || "",
        },
        body: JSON.stringify(curriculumData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        setError(errorData.error || "Failed to import curriculum.");
        setIsSubmitting(false);
        return;
      }

      navigate("/curriculums");
    } catch (err) {
      setError("An unexpected error occurred.");
      setIsSubmitting(false);
    }
  };

  return (
    <div className={styles.container}>
      <h1 className={styles.title}>Import Curriculum</h1>
      {error && <p className={styles.errorMessage}>{error}</p>}
      
      <form onSubmit={handleSubmit} className={styles.form}>
        <div className={styles.formGroup}>
          <label className={styles.label}>Curriculum Name:</label>
          <input
            ref={nameInputRef}
            type="text"
            value={name}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setName(e.target.value)}
            required
            className={styles.input}
            placeholder="Enter curriculum name"
          />
        </div>
        
        <div className={styles.formGroup}>
          <label className={styles.label}>Import Text:</label>
          <textarea
            value={importText}
            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setImportText(e.target.value)}
            className={styles.textarea}
            placeholder="Enter curriculum structure in the format:
level 1
- skill 1
- skill 2
level 2
- skill 3
- skill 4"
            rows={10}
          />
        </div>
        
        <div className={styles.formGroup}>
          <button 
            type="button" 
            onClick={handlePreview} 
            className={styles.previewButton}
            disabled={!importText.trim()}
          >
            Preview
          </button>
        </div>
        
        {previewData && (
          <div className={styles.previewContainer}>
            <h2 className={styles.previewTitle}>Preview</h2>
            {previewData.map((level, levelIndex) => (
              <div key={levelIndex} className={styles.levelPreview}>
                <h3 className={styles.levelName}>{level.name}</h3>
                <ul className={styles.skillsList}>
                  {level.skills.map((skill, skillIndex) => (
                    <li key={skillIndex} className={styles.skillItem}>{skill.name}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        )}
        
        <div className={styles.formGroup}>
          <button 
            type="submit" 
            className={styles.submitButton}
            disabled={isSubmitting || !previewData}
          >
            {isSubmitting ? "Importing..." : "Import Curriculum"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ImportCurriculumPage; 